import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import {
  getCourseById,
  createCourseRazorpayOrder,
  verifyCourseRazorpayPayment,
  enrollFreeCourse,
  getMyCoursePurchases,
  getCourseReviews,
  submitCourseReview,
  getCourseLessonProgress,
  updateCourseLessonStatus,
} from '../../../api';
import Dialog from '../../../components/Dialog';
import { Play, Star, HelpCircle, CreditCard, Lock, Share2, Clock, Users, Award, CheckCircle2, ChevronRight, Info, BookOpen } from 'lucide-react';
import { getVideoEmbedUrl } from '../../../utils/video';
import { getUserCourseSectionPaths } from '../../../utils/userCoursePaths';
import { loadRazorpayScript, formatRazorpayVerifyError } from '../../../utils/courseCheckout';
import PageLoader from '../../../components/PageLoader';


const formatRupee = (value) => {
  const n = Number(String(value ?? '').replace(/[^0-9.]/g, ''));
  if (!Number.isFinite(n)) return '—';
  return `₹${n.toLocaleString('en-IN')}`;
};

/** YouTube/Cloudinary thumbnail for lesson preview. */
const getLessonThumbnailUrl = (videoUrl) => {
  if (!videoUrl || typeof videoUrl !== 'string') return null;
  const u = videoUrl.trim();

  // YouTube
  const watch = u.match(/[?&]v=([\w-]{11})/);
  if (watch) return `https://img.youtube.com/vi/${watch[1]}/mqdefault.jpg`;
  const short = u.match(/youtu\.be\/([\w-]{11})/);
  if (short) return `https://img.youtube.com/vi/${short[1]}/mqdefault.jpg`;

  // Cloudinary
  if (u.includes('cloudinary.com') && u.includes('/video/upload/')) {
    // Replace /video/upload/ with /video/upload/c_fill,h_180,w_320/ and change extension to .jpg
    return u.replace('/video/upload/', '/video/upload/c_fill,h_180,w_320/').replace(/\.[^/.]+$/, '.jpg');
  }

  return null;
};

const shareCourseLink = async (e, courseId, courseTitle, courseBase) => {
  e.preventDefault();
  e.stopPropagation();
  const path = courseBase || `/user/courses/${courseId}`;
  const url = `${window.location.origin}${path}`;
  const title = courseTitle || 'Course';
  const text = `Check out: ${title}`;
  try {
    if (navigator.share) {
      await navigator.share({ title, text, url });
      return;
    }
  } catch (err) {
    if (err?.name === 'AbortError') return;
  }
  try {
    await navigator.clipboard.writeText(url);
  } catch {
    /* ignore */
  }
};

const UserCourseDetail = () => {
  const { courseId } = useParams();
  const location = useLocation();
  const { courseBase, listBase, listLabel, quizPath } = getUserCourseSectionPaths(
    location.pathname,
    courseId
  );
  const {
    user,
    getCourses,
    getEnrolledCourseIds,
    refreshUser,
    enrollInCourse,
  } = useAuth();
  const [course, setCourse] = useState(null);
  const [courseLoading, setCourseLoading] = useState(true);
  const [activeLesson, setActiveLesson] = useState(null);
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState('');
  const [ratingSaved, setRatingSaved] = useState(false);
  const [ratingError, setRatingError] = useState('');
  const [dbPurchasedSet, setDbPurchasedSet] = useState(new Set());
  const [courseReviews, setCourseReviews] = useState([]);
  const [payError, setPayError] = useState('');
  const [payLoading, setPayLoading] = useState(false);
  /** Shown after successful purchase / free enrol — stay on this page; user opens Purchased when they want */
  const [unlockSuccessMessage, setUnlockSuccessMessage] = useState('');
  const [lessonStatusByIndex, setLessonStatusByIndex] = useState({});
  const [progressError, setProgressError] = useState('');
  const [updatingLessonIndex, setUpdatingLessonIndex] = useState(null);
  const [expandedLessons, setExpandedLessons] = useState(new Set());

  const toggleExpandedLesson = (e, idx) => {
    e.stopPropagation();
    setExpandedLessons((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const enrolledIds = user?.id ? getEnrolledCourseIds(user.id) : [];
  const isEnrolled = enrolledIds.some((id) => String(id) === String(courseId));
  const subscriptionPlan = user?.subscriptionPlan || 'free';
  const allCourseRatings = React.useMemo(() => {
    const list = Array.isArray(courseReviews) ? courseReviews : [];
    return [...list].sort((a, b) => {
      const ta = new Date(a?.updatedAt || a?.createdAt || 0).getTime();
      const tb = new Date(b?.updatedAt || b?.createdAt || 0).getTime();
      return tb - ta;
    });
  }, [courseReviews]);
  const existingRating = user?.id
    ? allCourseRatings.find((r) => String(r.userId) === String(user.id)) || null
    : null;

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setCourseLoading(true);
      const list = getCourses() || [];
      const local = list.find((x) => String(x.id) === String(courseId));
      if (local) {
        if (!cancelled) {
          setCourse(local);
          setCourseLoading(false);
        }
        return;
      }
      try {
        const apiCourse = await getCourseById(courseId);
        if (!cancelled) setCourse(apiCourse || null);
      } catch {
        if (!cancelled) setCourse(null);
      } finally {
        if (!cancelled) setCourseLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [courseId, getCourses]);

  useEffect(() => {
    setUnlockSuccessMessage('');
  }, [courseId]);

  useEffect(() => {
    if (existingRating) {
      setRating(existingRating.rating || 0);
      setReview(existingRating.review || '');
    }
  }, [existingRating]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const reviews = await getCourseReviews(courseId);
        if (!cancelled) setCourseReviews(Array.isArray(reviews) ? reviews : []);
      } catch {
        if (!cancelled) setCourseReviews([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.id) {
        if (!cancelled) setDbPurchasedSet(new Set());
        return;
      }
      try {
        const data = await getMyCoursePurchases();
        if (!cancelled) {
          setDbPurchasedSet(new Set((data?.purchasedCourseIds || []).map(String)));
        }
      } catch {
        if (!cancelled) setDbPurchasedSet(new Set());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const handleSubmitRating = async () => {
    if (!user?.id || !rating) return;
    setRatingError('');
    // The backend will verify if the user is enrolled (via purchase or subscription)
    // and return a 403 if not. We already show/hide the form based on hasAccess.
    try {
      await submitCourseReview(courseId, rating, review);
      const reviews = await getCourseReviews(courseId);
      setCourseReviews(Array.isArray(reviews) ? reviews : []);
      setRatingSaved(true);
    } catch (err) {
      setRatingSaved(false);
      setRatingError(err?.response?.data?.message || err?.message || 'Could not save review');
    }
  };

  const allLessons = course?.lessons || [];

  const freeSampleCourseId = React.useMemo(() => {
    const list = getCourses();
    if (!list || list.length === 0) return null;
    return list[0].id;
  }, [getCourses]);

  const isFreeSampleCourse =
    subscriptionPlan === 'free' && course && String(course.id) === String(freeSampleCourseId);

  const hasAccess = isEnrolled || isFreeSampleCourse;

  const numericPrice = course != null ? Number(course.price) : NaN;
  const isPaidCourse = Number.isFinite(numericPrice) && numericPrice > 0;

  const purchasedIdSet = React.useMemo(() => {
    const ids = Array.isArray(user?.purchasedCourseIds) ? user.purchasedCourseIds : [];
    return new Set(ids.map(String));
  }, [user?.purchasedCourseIds]);

  const subscriptionFreeIdSet = React.useMemo(() => {
    const ids = Array.isArray(user?.subscriptionFreeCourseIds)
      ? user.subscriptionFreeCourseIds
      : [];
    return new Set(ids.map(String));
  }, [user?.subscriptionFreeCourseIds]);

  const isPurchased = purchasedIdSet.has(String(courseId));
  const hasSubscriptionCourseAccess = subscriptionFreeIdSet.has(String(courseId));
  const canRateCourse = hasAccess;

  /** Paid course not yet owned via purchase or subscription-included access */
  const needsPaidPurchase =
    isPaidCourse && !isPurchased && !hasSubscriptionCourseAccess;

  const loginRedirect = `/login?redirect=${encodeURIComponent(courseBase)}`;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.id || !course?.id || !hasAccess) {
        if (!cancelled) setLessonStatusByIndex({});
        return;
      }
      try {
        const data = await getCourseLessonProgress(course.id);
        if (cancelled) return;
        const next = {};
        (data?.lessonStatuses || []).forEach((row) => {
          next[row.lessonIndex] = row.status === 'completed' ? 'completed' : 'pending';
        });
        setLessonStatusByIndex(next);
        setProgressError('');
      } catch (err) {
        if (!cancelled) {
          setLessonStatusByIndex({});
          setProgressError(err?.response?.data?.message || err?.message || 'Could not load lesson progress');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, course?.id, hasAccess]);

  const getLessonStatus = (idx) =>
    lessonStatusByIndex[idx] === 'completed' ? 'completed' : 'pending';

  const toggleLessonStatus = async (idx) => {
    if (!course?.id || !user?.id) return;
    const current = getLessonStatus(idx);
    const next = current === 'completed' ? 'pending' : 'completed';
    setUpdatingLessonIndex(idx);
    setProgressError('');
    try {
      await updateCourseLessonStatus(course.id, idx, next);
      setLessonStatusByIndex((prev) => ({ ...prev, [idx]: next }));
    } catch (err) {
      setProgressError(err?.response?.data?.message || err?.message || 'Could not update lesson status');
    } finally {
      setUpdatingLessonIndex(null);
    }
  };

  const handleVideoEnded = async () => {
    if (!activeLesson || !allLessons) return;
    const idx = allLessons.findIndex((l) => l.videoUrl === activeLesson.videoUrl);
    if (idx !== -1) {
      if (getLessonStatus(idx) !== 'completed') {
        await toggleLessonStatus(idx);
      }
      const nextIdx = idx + 1;
      if (nextIdx < allLessons.length) {
        setActiveLesson(allLessons[nextIdx]);
      }
    }
  };

  useEffect(() => {
    const handleMessage = (event) => {

      if (event.origin === "https://www.youtube.com") {
        try {
          const data = JSON.parse(event.data);
          if (data.event === "onStateChange" && data.info === 0) {
            handleVideoEnded();
          }
        } catch (e) { }
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [activeLesson, allLessons]);

  const handleGetCourse = async () => {
    if (!user?.id || !course?.id || !isPaidCourse) return;
    setPayError('');
    setPayLoading(true);
    try {
      const scriptOk = await loadRazorpayScript();
      if (!scriptOk) {
        setPayError('Unable to load Razorpay. Please check your internet and try again.');
        return;
      }

      const order = await createCourseRazorpayOrder(course.id);
      if (!order?.orderId || !order?.keyId) {
        setPayError('Unable to create payment order. Please try again.');
        return;
      }

      const rz = new window.Razorpay({
        key: order.keyId,
        amount: Number(order.amount),
        currency: order.currency || 'INR',
        name: 'Market Mavericks',
        description: `Course: ${course.title || 'Course'}`,
        order_id: order.orderId,
        prefill: {
          name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
          email: user.email || '',
          contact: user.mobile || user.number || '',
        },
        theme: { color: '#38915c' },
        handler: async function (response) {
          try {
            await verifyCourseRazorpayPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            await refreshUser();
            enrollInCourse(user.id, course.id);
            setUnlockSuccessMessage(
              `Payment successful. “${course.title || 'This course'}” is saved to your account — you can keep watching here, or open Purchased courses to see it in your list.`
            );
          } catch (verifyErr) {
            setPayError(formatRazorpayVerifyError(verifyErr));
          } finally {
            setPayLoading(false);
          }
        },
        modal: {
          ondismiss: () => {
            setPayLoading(false);
          },
        },
      });

      rz.on('payment.failed', (resp) => {
        const e = resp?.error || {};
        setPayError(
          [e.description, e.reason, e.step].filter(Boolean).join(' — ') ||
          'Payment failed. In test mode use Razorpay test cards only.'
        );
        setPayLoading(false);
      });

      rz.open();
    } catch (err) {
      setPayError(err?.response?.data?.message || err?.message || 'Unable to start payment');
      setPayLoading(false);
    }
  };

  const handleEnrolFree = async () => {
    if (!user?.id || !course?.id || isPaidCourse) return;
    setPayError('');
    setPayLoading(true);
    try {
      await enrollFreeCourse(course.id);
      await refreshUser();
      enrollInCourse(user.id, course.id);
      setUnlockSuccessMessage(
        `You’re enrolled. “${course.title || 'This course'}” appears under Purchased courses — keep learning here or open that tab anytime.`
      );
    } catch (err) {
      setPayError(err?.response?.data?.message || err?.message || 'Could not enrol. Please try again.');
    } finally {
      setPayLoading(false);
    }
  };

  if (courseLoading) {
    return <PageLoader fullScreen={false} />;
  }

  if (!course) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <p className="text-gray-500">Course not found.</p>
        <Link to={listBase} className="mt-4 text-primary-600 hover:underline">
          ← Back to {listLabel}
        </Link>
      </div>
    );
  }

  const lessons = allLessons;
  const quizProjectCount = (course.quiz || []).length > 0 ? 1 : 0;
  const lessonsUnlockBanner = isFreeSampleCourse && (lessons.length > 1 || quizProjectCount > 0);
  const allLessonsCompleted = lessons.length > 0 && lessons.every((_, idx) => getLessonStatus(idx) === 'completed');

  return (
    <div className="min-h-screen bg-gray-50/50 -mt-8 -mx-4 lg:-mx-6">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[#064e3b] via-[#047857] to-[#022c22] text-white pt-12 pb-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Background Decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary-600/10 rounded-full blur-3xl -mr-48 -mt-48" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-secondary-600/10 rounded-full blur-3xl -ml-32 -mb-32" />

        <div className="max-w-7xl 4xl:max-w-10xl mx-auto relative z-10">
          <div className="flex flex-col gap-6 lg:pr-[400px]">
            {/* Breadcrumbs */}
            <nav className="flex items-center gap-2 text-sm text-gray-400">
              <Link to={listBase} className="hover:text-primary-400 transition-colors">
                {listLabel}
              </Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-gray-200 truncate">{course.title}</span>
            </nav>

            {/* Title & Badges */}
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {course.courseType === 'crypto' && (
                  <span className="px-3 py-1 rounded-full bg-orange-500/20 text-orange-400 text-xs font-bold uppercase tracking-wider border border-orange-500/30">
                    Crypto
                  </span>
                )}
                {course.underGrowthPlan && (
                  <span className="px-3 py-1 rounded-full bg-primary-500/20 text-primary-400 text-xs font-bold uppercase tracking-wider border border-primary-500/30">
                    Growth Plan
                  </span>
                )}
                {isPaidCourse ? (
                  <span className="px-3 py-1 rounded-full bg-violet-500/20 text-violet-400 text-xs font-bold uppercase tracking-wider border border-violet-500/30">
                    Premium
                  </span>
                ) : (
                  <span className="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-bold uppercase tracking-wider border border-emerald-500/30">
                    Free Course
                  </span>
                )}
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight">
                {course.title}
              </h1>
              {course.description && (
                <p className="text-lg text-gray-300 max-w-2xl leading-relaxed line-clamp-3">
                  {course.description}
                </p>
              )}
            </div>

            {/* Hero Stats */}
            <div className="flex flex-wrap items-center gap-6 mt-2">
              <div className="flex items-center gap-2">
                <div className="flex items-center text-yellow-400">
                  <Star className="w-5 h-5 fill-current" />
                  <span className="ml-1.5 font-bold text-lg">{course.rating || '4.8'}</span>
                </div>
                <span className="text-gray-400 text-sm">(1,240 ratings)</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <Users className="w-5 h-5 text-primary-400" />
                <span className="font-medium">{course.studentCount || '5k+'} students</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <Clock className="w-5 h-5 text-primary-400" />
                <span className="font-medium">{course.duration || '12.5 hours'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-7xl 4xl:max-w-10xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 pb-10 relative z-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left Column: Content */}
          <div className="lg:col-span-8 space-y-8 order-2 lg:order-1">
            {/* Unlock Success Message */}
            {unlockSuccessMessage && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 flex flex-col sm:flex-row items-start gap-4 shadow-sm animate-in fade-in slide-in-from-top-4 duration-500">
                <div className="p-2 rounded-full bg-emerald-100 text-emerald-600">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <p className="text-emerald-900 font-medium leading-relaxed">{unlockSuccessMessage}</p>
                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <Link
                      to="/user/courses"
                      className="inline-flex items-center px-4 py-2 rounded-xl bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 transition-all shadow-md shadow-emerald-200"
                    >
                      View in My Courses
                    </Link>
                    <button
                      onClick={() => setUnlockSuccessMessage('')}
                      className="px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 rounded-xl transition-colors"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* About Section */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <Info className="w-6 h-6 text-primary-600" />
                What you'll learn
              </h2>
              <div className="prose prose-primary max-w-none text-gray-600 leading-relaxed">
                {course.description || "In this course, you will master the fundamentals and advanced strategies to excel in the market. We cover everything from technical analysis to risk management."}
              </div>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  "Master Market Trends",
                  "Risk Management Strategies",
                  "Live Trading Examples",
                  "Exclusive Community Access",

                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Curriculum */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Course Curriculum</h2>
                  <p className="text-sm text-gray-500 mt-1">{lessons.length} lessons • All levels</p>
                </div>
                {lessonsUnlockBanner && (
                  <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-orange-50 text-orange-700 text-xs font-bold border border-orange-100">
                    <Lock className="w-3.5 h-3.5" />
                    CONTENT LOCKED
                  </div>
                )}
              </div>

              {lessons.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <BookOpen className="w-8 h-8 text-gray-300" />
                  </div>
                  <p className="text-gray-500">No lessons available yet. Check back later.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {lessons.map((lesson, i) => {
                    const isLocked = !hasAccess && i > 0;
                    const isCurrentlyPlaying = activeLesson?.videoUrl === lesson.videoUrl;
                    const status = getLessonStatus(i);
                    const thumbUrl = getLessonThumbnailUrl(lesson.videoUrl);

                    const canPlay = Boolean(lesson.videoUrl) && !isLocked;

                    const openLesson = () => {
                      if (!canPlay) return;
                      setActiveLesson(lesson);
                    };

                    return (
                      <div
                        key={lesson.id || i}
                        className={`group p-4 transition-all ${canPlay ? 'hover:bg-primary-50/30 cursor-pointer' : ''} ${isCurrentlyPlaying ? 'bg-primary-50/50' : ''}`}
                        onClick={openLesson}
                      >
                        <div className="flex gap-4">
                          {/* Lesson Number/Thumb */}
                          <div className="relative w-24 sm:w-32 aspect-video rounded-xl overflow-hidden bg-gray-100 shrink-0 border border-gray-200">
                            {thumbUrl ? (
                              <img src={thumbUrl} alt="" className={`w-full h-full object-cover transition-transform duration-500 ${canPlay ? 'group-hover:scale-110' : 'grayscale brightness-50'}`} />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-gray-900">
                                <Play className="w-6 h-6 text-white/40" />
                              </div>
                            )}

                            {isLocked && (
                              <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-[2px]">
                                <Lock className="w-6 h-6 text-white" />
                              </div>
                            )}

                            {canPlay && !isCurrentlyPlaying && (
                              <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-colors">
                                <div className="w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center scale-0 group-hover:scale-100 transition-transform duration-300">
                                  <Play className="w-5 h-5 text-primary-600 fill-current" />
                                </div>
                              </div>
                            )}

                            {isCurrentlyPlaying && (
                              <div className="absolute inset-0 flex items-center justify-center bg-primary-600/20">
                                <div className="flex gap-1 items-end h-4">
                                  <div className="w-1 bg-white rounded-full animate-bounce h-2" />
                                  <div className="w-1 bg-white rounded-full animate-bounce h-4" />
                                  <div className="w-1 bg-white rounded-full animate-bounce h-3" />
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0 flex flex-col justify-center">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-[10px] font-bold text-primary-600 uppercase tracking-widest">Lesson {i + 1}</span>
                              {status === 'completed' && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />}
                            </div>
                            <h3 className={`font-bold text-sm sm:text-base truncate ${isCurrentlyPlaying ? 'text-primary-700' : 'text-gray-900'}`}>
                              {lesson.title || 'Untitled Lesson'}
                            </h3>
                            <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500">
                              <div className="flex items-center gap-1">
                                <Clock className="w-3.5 h-3.5" />
                                <span>{lesson.duration || '10:00'}</span>
                              </div>
                              {isLocked ? (
                                <span className="text-orange-600 font-medium">LOCKED</span>
                              ) : (
                                <span className="text-emerald-600 font-medium">READY TO WATCH</span>
                              )}
                            </div>
                          </div>

                          {/* Toggle Progress (only if enrolled) */}
                          {hasAccess && !isLocked && (
                            <div className="flex items-center">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleLessonStatus(i);
                                }}
                                disabled={updatingLessonIndex === i}
                                className={`p-2 rounded-xl transition-all ${status === 'completed' ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100 text-gray-400 hover:bg-emerald-50 hover:text-emerald-500'}`}
                                title={status === 'completed' ? "Mark as pending" : "Mark as completed"}
                              >
                                {updatingLessonIndex === i ? (
                                  <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <CheckCircle2 className="w-5 h-5" />
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Quiz Section */}
          </div>

          {/* Right Column: Sidebar */}
          <div className="lg:col-span-4 order-1 lg:order-2 ">
            <div className=" lg:top-20 space-y-6 z-30 ">
              {/* Main Action Card */}
              <div className="bg-white rounded-[32px] shadow-xl shadow-gray-200/50 border border-gray-100 overflow-hidden">
                <div className="relative aspect-video">
                  {course.imageUrl ? (
                    <img src={course.imageUrl} alt={course.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-primary-600 to-secondary-600" />
                  )}
                  <div className="absolute inset-0 bg-black/20" />
                  {!activeLesson && hasAccess && (
                    <button
                      onClick={() => setActiveLesson(lessons[0])}
                      className="absolute inset-0 flex items-center justify-center group"
                    >
                      <div className="w-16 h-16 rounded-full bg-white/30 backdrop-blur-md flex items-center justify-center transition-transform group-hover:scale-110 duration-300">
                        <Play className="w-8 h-8 text-white fill-current" />
                      </div>
                    </button>
                  )}
                </div>

                <div className="p-6">
                  {needsPaidPurchase ? (
                    <div className="space-y-6">
                      <div className="flex items-baseline gap-3">
                        <span className="text-4xl font-black text-gray-900">{formatRupee(course.price)}</span>
                        {Number(course.originalPrice) > 0 && Number(course.originalPrice) > Number(course.price) && (
                          <span className="text-lg text-gray-400 line-through">{formatRupee(course.originalPrice)}</span>
                        )}
                        {Number(course.originalPrice) > 0 && Number(course.originalPrice) > Number(course.price) && (
                          <span className="ml-auto text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded">
                            {Math.round(((Number(course.originalPrice) - Number(course.price)) / Number(course.originalPrice)) * 100)}% OFF
                          </span>
                        )}
                      </div>

                      <div className="space-y-2">
                        {!user?.id ? (
                          <Link
                            to={loginRedirect}
                            className="flex items-center justify-center gap-3 w-full py-4 rounded-2xl bg-primary-600 text-white font-bold hover:bg-primary-700 transition-all shadow-lg shadow-primary-200"
                          >
                            <CreditCard className="w-5 h-5" />
                            Log in to Purchase
                          </Link>
                        ) : (
                          <button
                            onClick={handleGetCourse}
                            disabled={payLoading}
                            className="flex items-center justify-center gap-3 w-full py-4 rounded-2xl bg-primary-600 text-white font-bold hover:bg-primary-700 transition-all shadow-lg shadow-primary-200 disabled:opacity-60"
                          >
                            {payLoading ? (
                              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <>
                                <CreditCard className="w-5 h-5" />
                                Enroll Now
                              </>
                            )}
                          </button>
                        )}
                        <p className="text-center text-[10px] text-gray-400 font-medium">30-Day Money-Back Guarantee</p>
                      </div>
                    </div>
                  ) : !hasAccess && !isPaidCourse ? (
                    <div className="space-y-6">
                      <div className="text-4xl font-black text-gray-900">FREE</div>
                      <div className="space-y-3">
                        {!user?.id ? (
                          <Link
                            to={loginRedirect}
                            className="flex items-center justify-center w-full py-4 rounded-2xl bg-primary-600 text-white font-bold hover:bg-primary-700 transition-all shadow-lg shadow-primary-200"
                          >
                            Log in to Enroll
                          </Link>
                        ) : (
                          <button
                            onClick={handleEnrolFree}
                            disabled={payLoading}
                            className="flex items-center justify-center w-full py-4 rounded-2xl bg-primary-600 text-white font-bold hover:bg-primary-700 transition-all shadow-lg shadow-primary-200"
                          >
                            {payLoading ? "Enrolling..." : "Enroll for Free"}
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm font-bold mb-1">
                          <span className="text-gray-600">Your Progress</span>
                          <span className="text-primary-600">{Math.round((Object.values(lessonStatusByIndex).filter(s => s === 'completed').length / lessons.length) * 100) || 0}%</span>
                        </div>
                        <div className="h-2.5 w-full bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary-600 transition-all duration-1000"
                            style={{ width: `${(Object.values(lessonStatusByIndex).filter(s => s === 'completed').length / lessons.length) * 100 || 0}%` }}
                          />
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          const nextLesson = lessons.find((_, idx) => getLessonStatus(idx) !== 'completed') || lessons[0];
                          if (nextLesson) setActiveLesson(nextLesson);
                        }}
                        className="flex items-center justify-center gap-3 w-full py-4 rounded-2xl bg-primary-600 text-white font-bold hover:bg-primary-700 transition-all shadow-lg shadow-primary-200"
                      >
                        <Play className="w-5 h-5 fill-current" />
                        Continue Learning
                      </button>
                    </div>
                  )}

                  <div className="mt-8 pt-6 border-t border-gray-100 space-y-4">
                    <h4 className="text-sm font-bold text-gray-900">This course includes:</h4>
                    <div className="space-y-3">
                      {[
                        { icon: Play, text: "Full lifetime access" },
                        { icon: BookOpen, text: `${lessons.length} video lessons` },
                        { icon: Award, text: "Certificate of completion" }
                      ].map((item, i) => (
                        <div key={i} className="flex items-center gap-3 text-gray-600">
                          <item.icon className="w-4 h-4 text-primary-500" />
                          <span className="text-xs font-medium">{item.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Share Card */}
              <button
                onClick={(e) => shareCourseLink(e, courseId, course.title, courseBase)}
                className="w-full flex items-center justify-center gap-3 py-4 rounded-2xl bg-white border border-gray-200 text-gray-700 font-bold hover:bg-gray-50 transition-colors shadow-sm"
              >
                <Share2 className="w-5 h-5" />
                Share this course
              </button>

              {/* Final Assessment Card */}
              {(course.quiz || []).length > 0 && (
                <div className="bg-gradient-to-br from-[#064e3b] via-[#047857] to-[#065f46] rounded-[24px] p-6 text-white shadow-lg shadow-emerald-200">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                      <Award className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg leading-tight">Final Assessment</h3>
                      <p className="text-[10px] text-white/70 font-medium uppercase tracking-wider">Earn your certificate</p>
                    </div>
                  </div>

                  {allLessonsCompleted ? (
                    <Link
                      to={quizPath}
                      className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl bg-white text-[#047857] font-bold hover:bg-emerald-50 transition-all shadow-md"
                    >
                      Take Quiz Now
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  ) : (
                    <div className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl bg-black/10 text-white/60 text-xs font-bold cursor-not-allowed border border-white/10">
                      <Lock className="w-3.5 h-3.5" />
                      Complete lessons to unlock
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Reviews Section */}
      {/* Enhanced Reviews Section */}
      <div className="bg-white border-t border-gray-100 py-10">
        <div className="max-w-7xl 4xl:max-w-10xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Reviews Summary & Form */}
            <div className="lg:col-span-5 space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Course Rating</h2>
                <div className="flex items-center gap-6 mb-8">
                  <div className="flex flex-col items-center justify-center w-24 h-24 rounded-2xl bg-primary-50 border border-primary-100 shadow-sm">
                    <div className="text-3xl font-bold text-primary-700">{course.rating || '4.8'}</div>
                    <div className="text-[10px] font-bold text-primary-600 uppercase tracking-widest mt-0.5">out of 5</div>
                  </div>
                  <div>
                    <div className="flex items-center text-yellow-400 mb-1">
                      {[1, 2, 3, 4, 5].map(i => <Star key={i} className={`w-5 h-5 ${i <= (course.rating || 5) ? 'fill-current' : 'text-gray-100'}`} />)}
                    </div>
                    <p className="text-gray-500 text-sm font-medium">{courseReviews.length} Verified student reviews</p>
                  </div>
                </div>

              </div>

              {canRateCourse && !existingRating && (
                <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm relative overflow-hidden group">
                  <div className="relative z-10">
                    <h3 className="text-xl font-bold text-gray-900 mb-1">Write a Review</h3>
                    <p className="text-sm text-gray-500 mb-6">Share your learning experience with others.</p>

                    <div className="flex justify-center gap-2 mb-6">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <button
                          key={s}
                          onClick={() => setRating(s)}
                          className={`p-1.5 rounded-xl transition-all hover:scale-110 ${s <= rating ? 'text-yellow-400 bg-yellow-50 shadow-sm' : 'text-gray-200 hover:text-gray-300'}`}
                        >
                          <Star className={`w-8 h-8 ${s <= rating ? 'fill-current' : ''}`} strokeWidth={1.5} />
                        </button>
                      ))}
                    </div>

                    <textarea
                      value={review}
                      onChange={(e) => setReview(e.target.value)}
                      placeholder="Your feedback helps fellow students..."
                      className="w-full p-4 rounded-2xl border border-gray-100 bg-gray-50/50 focus:ring-4 focus:ring-primary-100 focus:border-primary-500 transition-all text-sm resize-none mb-4 min-h-[120px]"
                    />

                    {ratingError && <p className="text-red-600 text-[10px] mb-3 font-bold flex items-center gap-1.5 bg-red-50 p-2 rounded-lg"><Info className="w-3.5 h-3.5" />{ratingError}</p>}
                    {ratingSaved && <p className="text-emerald-600 text-[10px] mb-3 font-bold flex items-center gap-1.5 bg-emerald-50 p-2 rounded-lg"><CheckCircle2 className="w-3.5 h-3.5" />Published successfully!</p>}

                    <button
                      onClick={handleSubmitRating}
                      disabled={!rating}
                      className="w-full py-3.5 rounded-xl bg-primary-600 text-white font-bold text-sm hover:bg-primary-700 transition-all shadow-lg shadow-primary-200 disabled:opacity-50"
                    >
                      {existingRating ? 'Update Review' : 'Submit Review'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Reviews List */}
            <div className="lg:col-span-7">
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-xl font-bold text-gray-900">Member Reviews</h3>
                <div className="text-xs font-bold text-primary-600 hover:underline cursor-pointer">
                  Most Relevant
                </div>
              </div>

              {courseReviews.length === 0 ? (
                <div className="py-16 text-center bg-gray-50 rounded-3xl border border-dashed border-gray-200">
                  <Star className="w-10 h-10 text-gray-200 mx-auto mb-4" />
                  <h4 className="text-lg font-bold text-gray-400">No reviews yet</h4>
                  <p className="text-xs text-gray-400 mt-1">Be the first to review this course!</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {courseReviews.map((rev, i) => {
                    const initials = (rev.userName || 'U').charAt(0).toUpperCase();
                    const themeColors = [
                      'bg-emerald-50 text-emerald-600 border-emerald-100',
                      'bg-blue-50 text-blue-600 border-blue-100',
                      'bg-violet-50 text-violet-600 border-violet-100',
                      'bg-orange-50 text-orange-600 border-orange-100',
                      'bg-rose-50 text-rose-600 border-rose-100'
                    ];
                    const colorClass = themeColors[i % themeColors.length];

                    return (
                      <div key={rev.id || i} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all group">
                        <div className="flex items-start justify-between gap-4 mb-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm border ${colorClass}`}>
                              {initials}
                            </div>
                            <div>
                              <div className="text-sm font-bold text-gray-900 flex items-center gap-2">
                                {rev.userName || 'Learner'}
                                <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600 text-[8px] font-bold uppercase tracking-wider border border-emerald-100">
                                  Verified
                                </span>
                              </div>
                              <div className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-0.5">{new Date(rev.updatedAt || rev.createdAt || Date.now()).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 bg-yellow-50 px-2 py-1 rounded-lg border border-yellow-100">
                            <span className="text-xs font-bold text-yellow-700">{rev.rating}.0</span>
                            <Star className="w-3 h-3 text-yellow-500 fill-current" />
                          </div>
                        </div>

                        <p className="text-gray-600 text-sm leading-relaxed italic pr-4">
                          &ldquo;{rev.review || "Excellent content and very well explained!"}&rdquo;
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Dialog
        open={Boolean(activeLesson)}
        onClose={() => setActiveLesson(null)}
        title={activeLesson?.title || "Lesson Video"}
        size="xl"
        footer={null}
      >
        {activeLesson?.videoUrl && (() => {
          const embedUrl = getVideoEmbedUrl(activeLesson.videoUrl);
          if (embedUrl) {
            return (
              <div
                className="relative w-full aspect-video sm:h-[350px] md:h-[450px] lg:h-[500px] h-[250px] mx-auto rounded-lg overflow-hidden bg-black shadow-lg"
              >
                <iframe
                  src={embedUrl}
                  title={activeLesson.title || "Lesson video"}
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            );
          }
          return (
            <video
              src={activeLesson.videoUrl}
              controls
              autoPlay
              onEnded={handleVideoEnded}
              className="w-full aspect-video sm:h-[350px] md:h-[450px] lg:h-[500px] h-[250px] mx-auto rounded-lg bg-black object-contain shadow-lg"
              playsInline
            >
              Your browser does not support the video tag.
            </video>
          );
        })()}
      </Dialog>
    </div>
  );
};

export default UserCourseDetail;
